import React, { useCallback } from 'react';
import PropTypes from 'prop-types';
import { Grid, FormControlLabel, Switch } from '@material-ui/core';
import { Formik, Form } from 'formik';
import { isUndefined } from 'lodash';
import { useSnackbar } from 'notistack';

import CardWithProgressLoading from '../CardWithProgressLoading';
import ButtonWithCirculaProgress from '../ButtonWithCirculaProgress';
import QrSaleOrderModal from '../QrSaleOrderModal';
import { useToggleModalWithData, useLazyRequest } from '../../hooks';
import { updateConfigUser, generateQRArsAdvanced } from '../../api';

const getValues = ({ active, toDonations, useQrSalesOrder }) => ({
  active,
  ...(!isUndefined(toDonations) ? { toDonations } : {}),
  useQrSalesOrder: Boolean(useQrSalesOrder),
});

const ConfigEditUser = ({
  id,
  active,
  toDonations,
  useQrSalesOrder,
  refetchData,
}) => {
  const { enqueueSnackbar } = useSnackbar();

  const handleSubmit = useCallback(
    async (values) => {
      try {
        await updateConfigUser({ id, data: values });

        enqueueSnackbar('Usuario actualizado correctamente', {
          variant: 'success',
          key: 'successEditUserConfig',
        });

        refetchData?.();
      } catch (err) {
        enqueueSnackbar('Hubo error, intentelo nuevamente', {
          variant: 'error',
          key: 'errorEditUserConfig',
        });
      }
    },
    [id, enqueueSnackbar, refetchData],
  );

  const [
    openQrSaleOrderModal,
    qrSaleOrderData,
    handleOpenQrSaleOrderModal,
    handleCloseQrSaleOrderModal,
  ] = useToggleModalWithData({
    initialData: {},
    initialOpen: false,
  });

  const [, loadingGetQrSaleOrder, , executeGetQrSaleOrder] = useLazyRequest({
    request: generateQRArsAdvanced,
    withPostSuccess: (response) => {
      handleOpenQrSaleOrderModal({
        imageUrl: response.data.image_url,
        code: response.data.code,
      });
    },
    withPostFailure: () => {
      enqueueSnackbar(
        'Ha ocurrido un error, intententelo nuevamente mas tarde',
        { variant: 'error' },
      );
    },
  });

  const handleGetQrSaleOrder = useCallback(async () => {
    await executeGetQrSaleOrder(id);
  }, [executeGetQrSaleOrder, id]);

  return (
    <CardWithProgressLoading loading={false}>
      <Formik
        initialValues={getValues({ active, toDonations, useQrSalesOrder })}
        onSubmit={handleSubmit}
      >
        {({ values, handleChange, isSubmitting, dirty }) => (
          <Grid item container component={Form} spacing={3} justify="center">
            <Grid item xs={12} container spacing={3}>
              <Grid item xs={12}>
                <FormControlLabel
                  control={
                    <Switch
                      name="active"
                      checked={values.active}
                      onChange={handleChange}
                      color="primary"
                    />
                  }
                  label="Activo?"
                />
              </Grid>

              {!isUndefined(toDonations) && (
                <Grid item xs={12}>
                  <FormControlLabel
                    control={
                      <Switch
                        name="toDonations"
                        checked={values.toDonations}
                        onChange={handleChange}
                        color="primary"
                      />
                    }
                    label="Donaciones?"
                  />
                </Grid>
              )}

              {!isUndefined(useQrSalesOrder) && (
                <Grid item container xs={12} alignItems="center">
                  <Grid item xs="auto">
                    <FormControlLabel
                      control={
                        <Switch
                          name="useQrSalesOrder"
                          checked={values.useQrSalesOrder}
                          onChange={handleChange}
                          color="primary"
                        />
                      }
                      label="QR en pesos Avanzado?"
                    />
                  </Grid>

                  {useQrSalesOrder && (
                    <Grid item xs="auto">
                      <ButtonWithCirculaProgress
                        variant="outlined"
                        color="default"
                        label="Mostrar QR"
                        gutters={false}
                        handleButtonClick={handleGetQrSaleOrder}
                        loading={loadingGetQrSaleOrder}
                      />
                    </Grid>
                  )}
                </Grid>
              )}

              <Grid item container justify="center">
                <Grid item xs="auto">
                  <ButtonWithCirculaProgress
                    type="submit"
                    loading={isSubmitting}
                    disabled={!dirty}
                    label="Guardar"
                  />
                </Grid>
              </Grid>
            </Grid>
          </Grid>
        )}
      </Formik>

      <QrSaleOrderModal
        open={openQrSaleOrderModal}
        onClose={() => handleCloseQrSaleOrderModal()}
        imageUrl={qrSaleOrderData?.imageUrl}
        code={qrSaleOrderData?.code}
      />
    </CardWithProgressLoading>
  );
};

ConfigEditUser.propTypes = {
  id: PropTypes.string.isRequired,
  active: PropTypes.bool,
  toDonations: PropTypes.bool,
  useQrSalesOrder: PropTypes.bool,
  refetchData: PropTypes.func,
};

ConfigEditUser.defaultProps = {
  active: undefined,
  toDonations: undefined,
  useQrSalesOrder: undefined,
  refetchData: () => {},
};

export default ConfigEditUser;
